import axios, { AxiosResponse } from 'axios';

import { AuthProvider } from './auth.service';
import { environment } from '../environment';
import { Member } from '../components/admin/groupRole/MemberGroupRoleDialog';

export interface Group {
  name: string;
  id: string;
}

export interface BaseGroupRole {
  groupId: string;
  groupName: string;
  tenantId: string;
  titleId: string;
  subjectType: string;
  role: RoleType;
}

export enum RoleType {
  Admin = 'Admin',
  Moderator = 'Moderator'
}

export interface GroupRole extends BaseGroupRole {
  id: string;
}

const groupRoleApiUrl = `${environment.apiUrl}api/grouprole`;

const getGroupRolesRoute = (tenantId: string, titleId: string, subjectType: string): string => {
  return `${groupRoleApiUrl}/tenantId/${tenantId}/titleId/${titleId}/subjectType/${subjectType}`;
};
const insertGroupRoleRoute = groupRoleApiUrl + '/insert';
const updateGroupRoleRoute = groupRoleApiUrl + '/update';
const deleteGroupRoleRoute = groupRoleApiUrl + '/delete';
const rbacSyncRoute = groupRoleApiUrl + '/sync';
const getUserByGroupRoute = (groupId: string): string => {
  return `${groupRoleApiUrl}/getGroupMembers/groupId/${groupId}`;
};

const getGroupIdByNameRoute = (groupDisplayName: string): string => {
  return `${groupRoleApiUrl}/getGroups/groupDisplayName/${groupDisplayName}`;
};

const isMock = JSON.parse(environment.useMock) as boolean;

// GetGroupRoles
// GET: {{baseUrl}}/api/grouprole/tenantId/{{tenantId}}/titleId/{{titleId}}/subjectType/{{subjectType}}
export const getGroupRoles = async (tenantId: string, titleId: string, subjectType: string): Promise<GroupRole[]> => {
  if (!isMock) {
    await AuthProvider.setToken();
  }
  const tempURL = getGroupRolesRoute(tenantId, titleId, subjectType);
  const response: AxiosResponse<GroupRole[]> = await axios.get(tempURL);
  return response.data;
};

// InsertGroupRole
// POST: {{baseUrl}}/api/grouprole/insert
// {
//     "groupId": "{{groupId}}",
//     "groupName": "{{groupName}}",
//     "tenantId": "{{tenantId}}",
//     "titleId": "{{titleId}}",
//     "subjectType": "{{subjectType}}",
//     "role": "{{testRoleName}}"
// }
export const insertGroupRole = async (groupRole: BaseGroupRole): Promise<GroupRole> => {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<GroupRole> = await axios.post(insertGroupRoleRoute, groupRole);
  return response.data;
};

// UpdateGroupRole
// PUT: {{baseUrl}}/api/grouprole/update
// {
//     "id": "{{id}}",
//     "groupId": "{{groupId}}",
//     "groupName": "{{groupName}}",
//     "tenantId": "{{tenantId}}",
//     "titleId": "{{titleId}}",
//     "subjectType": "{{subjectType}}",
//     "role": "{{testRoleName}}"
// }
export const updateGroupRole = async (groupRole: GroupRole): Promise<GroupRole> => {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<GroupRole> = await axios.put(updateGroupRoleRoute, groupRole);
  return response.data;
};

// DeleteGroupRole
// DELETE: {{baseUrl}}/api/grouprole/delete
// {
//     "id": "{{id}}",
//     "groupId": "{{groupId}}",
//     "groupName": "{{groupName}}",
//     "tenantId": "{{tenantId}}",
//     "titleId": "{{titleId}}",
//     "subjectType": "{{subjectType}}",
//     "role": "{{testRoleName}}"
// }
export const deleteGroupRole = async (groupRole: GroupRole): Promise<void> => {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  await axios.delete(
    `${deleteGroupRoleRoute}/tenantId/${groupRole.tenantId}/titleId/${groupRole.titleId}/subjectType/${groupRole.subjectType}/groupId/${groupRole.groupId}/id/${groupRole.id}`
  );
};

// RbacSync
// POST: {{baseUrl}}/api/grouprole/sync
// {
//     "tenantId" : "{{tenantId}}",
//     "titleId": "{{titleId}}",
//     "subjectType": "{{subjectType}}"
// }
export const rbacSync = async (tenantId: string, titleId: string, subjectType: string): Promise<void> => {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  await axios.post(rbacSyncRoute, {
    tenantId,
    titleId,
    subjectType
  });
};

// the array returned is a list of aliases that are members in the group
export async function usersByGroup(groupId: string): Promise<Member[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<Member[]> = await axios.get(getUserByGroupRoute(groupId));
  return response.data;
}

// ideally this returns a list of length one, but there may be more than one match for a given groupDisplayName
export async function getGroupId(groupDisplayName: string): Promise<Group[]> {
  if (!isMock) {
    await AuthProvider.setToken();
  }

  const response: AxiosResponse<Group[]> = await axios.get(getGroupIdByNameRoute(groupDisplayName));
  return response.data;
}
